import axios from "axios"
import { VITE_AEM_URL, VITE_AEM_USE_FALLBACK } from "../utils/constants.util"
import {
	AEMPageType,
	AEMPagePaths,
	EntityPathParams,
	AEMOptions
} from "./models/AEMPage.model"
import { useEffect } from "react"
import { AppEvents, QueryKeys } from "@utils/user-config"
import footerData from "./models/fallbackData/footer.json"
import homeData from "./models/fallbackData/home.json"
import loginData from "./models/fallbackData/login.json"
import seeOurShipsData from "./models/fallbackData/seeOurShips.json"
import seeOurShipsOverview from "./models/fallbackData/seeOurShipsOverview.json"
import seeOurShipsFleet from "./models/fallbackData/seeOurShipsFleet.json"
import seeOurShipsDining from "./models/fallbackData/seeOurShipsDining.json"
import seeOurShipsSpecialtyDining from "./models/fallbackData/seeOurShipsSpecialtyDining.json"
import seeOurShipsComplimentaryDining from "./models/fallbackData/seeOurShipsComplimentaryDining.json"
import micrositeOverviewData from "./models/fallbackData/micrositeOverview.json"
import micrositeGalleryData from "./models/fallbackData/micrositeGallery.json"
import micrositeEventsData from "./models/fallbackData/micrositeEvents.json"
import { createDefaultMapper } from "./AEMContentMapper"
import { AEMResponse } from "./models/AEMResponse.model"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { useQueryClient } from "@tanstack/react-query"
import countdownBannerData from "./models/fallbackData/countdownbanner.json"

export const queryClient = new QueryClient()

const fallbackData: Partial<Record<string, unknown>> = {
	[AEMPageType.HOME]: homeData,
	[AEMPageType.LOGIN]: loginData,
	[AEMPageType.FOOTER]: footerData,
	[AEMPageType.SEE_OUR_SHIPS]: seeOurShipsData,
	[AEMPageType.SEE_OUR_SHIPS_OVERVIEW]: seeOurShipsOverview,
	[AEMPageType.SEE_OUR_SHIPS_FLEET]: seeOurShipsFleet,
	[AEMPageType.SEE_OUR_SHIPS_DINING]: seeOurShipsDining,
	[AEMPageType.SEE_OUR_SHIPS_SPECIALTY_DINING]: seeOurShipsSpecialtyDining,
	[AEMPageType.SEE_OUR_SHIPS_COMPLIMENTARY_DINING]:
		seeOurShipsComplimentaryDining,
	[AEMPageType.MICROSITE_OVERVIEW]: micrositeOverviewData,
	[AEMPageType.MICROSITE_GALLERY]: micrositeGalleryData,
	[AEMPageType.MICROSITE_EVENTS]: micrositeEventsData,
	[AEMPageType.COUNTDOWN_BANNER]: countdownBannerData
}

export const AVAILABLE_UNAUTHENTICATED_PAGES = [
	AEMPageType.LOGIN,
	AEMPageType.ICONS,
	AEMPageType.FOOTER,
	AEMPageType.DICTIONARY,
	AEMPageType.LANGUAGES,
	AEMPageType.NAVIGATION_FOOTER,
	AEMPageType.PAGES_TERMS_OF_USE,
	AEMPageType.PAGES_PRIVACY_POLICY,
	AEMPageType.PASSWORD_RESET_LOGIN,
	AEMPageType.AGENT_REGISTRATION_HOME,
	AEMPageType.AGENT_REGISTRATION_FOUND,
	AEMPageType.AGENT_REGISTRATION_EMAIL,
	AEMPageType.AGENT_REGISTRATION_VERIFICATION_SUCCESS,
	AEMPageType.PAGES_TRAVEL_AGENT_GUIDELINES,
	AEMPageType.TAGS,
	AEMPageType.AGENT_WELCOME,
	AEMPageType.AGENCY_EMAIL_VERIFICATION_SENT,
	AEMPageType.AGENCY_REGISTRATION_HOME,
	AEMPageType.AGENCY_EMAIL_VERIFICATION,
	AEMPageType.APP_SETTINGS,
	AEMPageType.AGENCY_SALES_CONTRACT,
	AEMPageType.ACCOUNT_FORGOT
]

const valueToLowerIfRequired = (key: string, value: string): string => {
	if (["country", "office", "role"].includes(key)) {
		return value?.toLowerCase()
	}
	return value
}

export const fetchAEMContent = async <T extends AEMResponse>(
	page: AEMPageType,
	options?: AEMOptions
): Promise<T> => {
	try {
		const queryParams = options?.queryParams
		const contentPath = queryParams?.contentPath ?? "cruising-power"
		let path = AEMPagePaths[page]
		if (options?.brand) {
			path = path.replace(EntityPathParams.BRAND, options.brand)
		}
		if (options?.ship) {
			path = path.replace(EntityPathParams.SHIP, `-${options.ship}`)
		} else if (options?.brand) {
			path = path.replace(EntityPathParams.SHIP, "")
		}
		if (options?.office) {
			path = path.replace(EntityPathParams.OFFICE, `${options.office}`)
		}

		const baseUrl = new URL(
			`${VITE_AEM_URL}/api/royal/content/${contentPath}${path}${(
				options?.pathParams || []
			)
				.map((value) => `/${value}`)
				.join("/")}`
		)
		baseUrl.search = new URLSearchParams(
			Object.entries(queryParams ?? {})
				.filter(([_, value]) => value !== undefined)
				.map(([key, value]) => [
					key,
					valueToLowerIfRequired(key, value as string)
				])
		).toString()

		const { data } = await axios.get<AEMResponse>(baseUrl.toString(), {
			headers: {
				"edge-cache-tag": `${page}-${queryParams?.locale}`
			},
			validateStatus: (status) => status === 200
		})

		const mapper = options?.mapper ?? createDefaultMapper()
		const parsedData = mapper.parse(data, options)

		return parsedData as T
	} catch (error) {
		console.error(`Error fetching AEM content for ${page}:`, error)

		const fallbackPageData = fallbackData[page as AEMPageType]
		// Santiago Ponce: Aem fallback meant only for debugging pruposes, do not allow on production
		if (fallbackPageData && VITE_AEM_USE_FALLBACK) {
			console.warn(`Using fallback data for ${page}`)
			return fallbackPageData as T
		}

		throw error
	}
}

export const AEMContentProvider: React.FC<{ children: React.ReactNode }> = ({
	children
}) => <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
export const useAEMContentRefresh = (pages: AEMPageType[]) => {
	const queryClient = useQueryClient()

	useEffect(() => {
		const refreshContent = (event: CustomEvent<{ locale: string }>) => {
			const locale = event.detail.locale
			for (const page of pages) {
				queryClient.invalidateQueries({
					queryKey: [QueryKeys.AEM_CONTENT, page, locale]
				})
			}
		}

		window.addEventListener(
			AppEvents.USER_LANGUAGE_CHANGED,
			refreshContent as EventListener
		)
		return () =>
			window.removeEventListener(
				AppEvents.USER_LANGUAGE_CHANGED,
				refreshContent as EventListener
			)
	}, [pages, queryClient])
}
